/*
 * Per-page boot for the coverage treatment.
 *
 * The FACE arrives via CSS (src/index.scss loads the font package), so every
 * page gets it for free. The run-origin SNAP is JavaScript and does not: it was
 * called from demo/client.tsx only, so seven of the eight entrypoints rendered
 * coverage with half its treatment. Measured on /kanban_ui before this existed:
 * 6 of 13 runs on the device grid.
 *
 * On Gecko the CSS floor alone is `isolation + multiply` WITHOUT the snap, which
 * measures chroma 0 but a 90% loss of the glyph's opaque core -- the black
 * skeleton dissolves into grey and neighbouring glyphs merge, reading as soft
 * text with broken kerning. Snap + blend together is the only combination that
 * passes all four gates (Notes/49 §1). So a page that skips this is not
 * "slightly untreated", it is visibly worse than plain text.
 *
 * Call AFTER mounting: the snapper measures live boxes. It re-snaps itself on
 * resize, DPR change, webfont load and DOM mutation.
 *
 * OPT-IN AS OF 2026-07-29 — see the header of pixel-perfect.css. The snap runs
 * only when <html> carries data-be-pixel-text="on", which is the SAME switch the
 * stylesheet's compositing levers key off. That shared gate is the point: the
 * paragraph above is the reason the two must never be independently switchable,
 * since blend-without-snap is worse than no treatment at all.
 *
 * A second reason to default it off: on mobile, a dynamic browser toolbar fires
 * visualViewport.resize continuously while scrolling, so every run recomputes a
 * different sub-pixel translate each frame and the page visibly wobbles on the
 * y-axis.
 */

import { beSnapInit } from "../packages/beos-fonts/src/be-snap.ts";

/** Is the coverage treatment switched on for this document? */
export function bePixelTextOn(doc: Document = document): boolean {
	return doc.documentElement.dataset.bePixelText === "on";
}

/*
 * The demo's OWN coverage runs. Component labels carry .be-pixel-text, but page
 * chrome does not and is spread across ~20 section files, so it is targeted by
 * selector instead. This is the SAME scope demo_shared.css sets the ladder on --
 * it lived in demo/client.tsx and so applied to the gallery alone, which is why
 * /fonts headings were never snapped either.
 *
 * These get the SNAP only, not the Gecko blend. The blend is mix-blend-mode:
 * multiply, which is identity for BLACK ink and ERASES WHITE ink -- and this
 * chrome is white-on-teal. So the core is fixed here while the ClearType fringe
 * is not: a strict improvement, not a complete one.
 */
const CHROME_RUNS = ".demo-content :is(h1, h2, h3, h4), .demo-topbar__title";

export function beBoot(opts: { also?: string } = {}): () => void {
	if (!bePixelTextOn()) return () => {};
	return beSnapInit(document, { also: opts.also ?? CHROME_RUNS });
}
